import { ComponentProps, HTMLAttributes } from 'react'
import { twMerge } from 'tailwind-merge'
import { Typography } from '../Typography'

interface SimpleTableProps extends HTMLAttributes<HTMLDivElement> {
    readonly border?: boolean
}

function SimpleTable({
    border = true,
    className,
    children,
    ...props
}: SimpleTableProps): JSX.Element {
    return (
        <div
            {...props}
            className={twMerge(
                'px-4 py-1 border',
                border ? 'rounded-xl' : '',
                className
            )}
        >
            {children}
        </div>
    )
}

interface SimpleTableRowProps extends HTMLAttributes<HTMLDivElement> {
    readonly border?: boolean
}

function SimpleTableRow({
    border,
    className,
    children,
    ...props
}: SimpleTableRowProps): JSX.Element {
    return (
        <div
            {...props}
            className={twMerge(
                'flex row items-center py-3',
                border && 'border-b border-dotted',
                className
            )}
        >
            {children}
        </div>
    )
}

interface SimpleTableColumnProps extends HTMLAttributes<HTMLDivElement> {}

function SimpleTableColumn({
    className,
    children,
    ...props
}: SimpleTableColumnProps): JSX.Element {
    return (
        <div
            {...props}
            className={twMerge('flex justify-center px-1', className)}
        >
            {children}
        </div>
    )
}

interface SimpleTableRowDefaultProps extends SimpleTableRowProps {
    readonly title?: string
    readonly weight?: ComponentProps<typeof Typography.Caption>['weight']
    readonly variant?: ComponentProps<typeof Typography.Caption>['variant']
    readonly size?: ComponentProps<typeof Typography.Caption>['size']
    readonly lineHeight?: ComponentProps<
        typeof Typography.Caption
    >['lineHeight']
}

function SimpleTableRowDefault({
    title,
    border,
    className,
    children,
    weight = 'font-semibold',
    variant,
    size,
    lineHeight,
    ...props
}: SimpleTableRowDefaultProps): JSX.Element {
    return (
        <SimpleTableRow {...props} border={border} className={className}>
            <SimpleTableColumn className="mr-auto">
                <Typography.Caption
                    weight={weight}
                    variant={variant}
                    size={size}
                    lineHeight={lineHeight}
                >
                    {title}
                </Typography.Caption>
            </SimpleTableColumn>
            <SimpleTableColumn>{children}</SimpleTableColumn>
        </SimpleTableRow>
    )
}

SimpleTableRow.Default = SimpleTableRowDefault

SimpleTable.Row = SimpleTableRow
SimpleTable.Column = SimpleTableColumn

export { SimpleTable }
